// components/FlashBar.jsx
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline'; // Light close icon

const FlashBar = ({ config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Default config if not provided (for testing)
  const defaultConfig = {
    enabled: true,
    preset: 'simple-promo',
    messages: [{ text: 'Free Shipping on orders over $50!' }],
    dismissible: true,
    devMode: false,
    customStyles: {},
  };

  const finalConfig = { ...defaultConfig, ...config };

  // Check visibility (schedule + dismissal)
  useEffect(() => {
    if (!finalConfig.enabled) return;

    const now = Date.now();

    // Schedule check
    if (finalConfig.schedule?.start && new Date(finalConfig.schedule.start) > new Date()) return;
    if (finalConfig.schedule?.end && new Date(finalConfig.schedule.end) < new Date()) return;

    // Dismissal persistence
    const dismissed = localStorage.getItem('flashbar_dismissed');
    if (dismissed && finalConfig.dismissible) {
      const { until } = JSON.parse(dismissed);
      if (until > now) {
        setIsDismissed(true);
        return;
      }
    }

    setIsVisible(true);
  }, [finalConfig]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    if (finalConfig.dismissible) {
      const until = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem('flashbar_dismissed', JSON.stringify({ until }));
    }
  };

  if (!isVisible || isDismissed || !finalConfig.enabled || finalConfig.messages.length === 0) {
    return null;
  }

  // Preset style mappings
  const presetStyles = {
    'simple-promo': {
      bg: 'bg-gray-100',
      text: 'text-gray-900',
      accent: 'bg-gray-900 text-white hover:bg-gray-800',
    },
    'free-shipping': {
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      accent: 'bg-emerald-600 text-white hover:bg-emerald-700',
    },
    'flash-sale-countdown': {
      bg: 'bg-red-100',
      text: 'text-red-800',
      accent: 'bg-red-600 text-white hover:bg-red-700',
    },
    'basic-text': {
      bg: 'bg-white border-b border-gray-200',
      text: 'text-gray-800',
      accent: 'bg-black text-white hover:bg-gray-900',
    },
    'icon-info': {
      bg: 'bg-amber-50',
      text: 'text-amber-900',
      accent: 'bg-amber-600 text-white hover:bg-amber-700',
    },
    'multi-rotating': {
      bg: 'bg-gray-50',
      text: 'text-gray-900',
      accent: 'bg-gray-800 text-white hover:bg-black',
    },
  };

  const style = finalConfig.devMode && finalConfig.customStyles?.backgroundColor
    ? {}
    : presetStyles[finalConfig.preset] || presetStyles['simple-promo'];

  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-50 flex items-center justify-center
        transition-all duration-500 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
        ${finalConfig.devMode ? '' : style.bg}
        ${finalConfig.devMode ? '' : style.text}
      `}
      style={{
        backgroundColor: finalConfig.devMode && finalConfig.customStyles.backgroundColor
          ? finalConfig.customStyles.backgroundColor
          : undefined,
        color: finalConfig.devMode && finalConfig.customStyles.textColor
          ? finalConfig.customStyles.textColor
          : undefined,
        height: finalConfig.customStyles?.height || '48px',
        fontSize: finalConfig.customStyles?.fontSize || '15px',
      }}
      role="alert"
      aria-live="polite"
    >
      <div className="container mx-auto px-4 flex items-center justify-center gap-4 relative max-w-7xl">
        
        {/* Preset: Simple Promo with CTA — First Implementation */}
        {finalConfig.preset === 'simple-promo' && (
          <div className="flex items-center justify-center gap-4 flex-1 text-center">
            <span className="font-medium">
              {finalConfig.messages[0].text}
            </span>

            {finalConfig.messages[0].ctaText && finalConfig.messages[0].link ? (
              <a
                href={finalConfig.messages[0].link}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition
                  ${finalConfig.devMode && finalConfig.customStyles.accentColor
                    ? ''
                    : style.accent}
                `}
                style={{
                  backgroundColor: finalConfig.devMode && finalConfig.customStyles.accentColor
                    ? finalConfig.customStyles.accentColor
                    : undefined,
                }}
              >
                {finalConfig.messages[0].ctaText}
              </a>
            ) : null}
          </div>
        )}

        {/* Close Button */}
        {finalConfig.dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition"
            aria-label="Close announcement"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FlashBar;